import React, { useState, useEffect, useRef } from "react";
import "./PolicyTracker.css";
import axios from "axios";
import { gapi } from "../../component/GlobalAPI";
import { useUserSession } from "../../component/useUserSession";
import Toast from "../../component/Toast";
import { useUser } from "../../../LevelContext";
import History from "../../component/histry/Htory";
import { Deleteic, Editic } from "../../component/UseIcons";

const PolicyTracker = ({ filterToPage, setSummaryInfo }) => {
  const contextuser = useUser();
  const valueuser = contextuser?.levelUser;

  // Use the new user session hook
  const {
    userData,
    isLoading: userLoading,
    error: userError,
  } = useUserSession();

  const [policies, setPolicies] = useState([]);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleted, setDeleted] = useState([]);
  const [recycleBin, setRecycleBin] = useState(false);
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openHistoryId, setOpenHistoryId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // New state for chat functionality
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousFormData, setPreviousFormData] = useState({});

  // Effect to automatically set filter when filterToPage changes
  useEffect(() => {
    if (filterToPage.trim() === "" || filterToPage) {
      console.log("🎯 Setting filter from prop:", filterToPage);
      setFilter(filterToPage);
    }
  }, [filterToPage]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Date range filter function
  const getDateRangeFilter = (filterText) => {
    if (!filterText) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filterLower = filterText.toLowerCase().trim();

    if (filterLower.includes("due this week")) {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      return { type: "week", start: startOfWeek, end: endOfWeek };
    }

    if (filterLower.includes("due this month")) {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      return { type: "month", start: startOfMonth, end: endOfMonth };
    }

    return null;
  };

  // Check if policy is pending
  const isPolicyPending = (policy) => {
    const status = (policy.status || "").toLowerCase();
    return status === "pending";
  };

  // Check if deadline is within date range
  const isDeadlineInRange = (deadline, start, end) => {
    if (!deadline || deadline === "N/A" || deadline === "") return false;

    try {
      const datePart = deadline.split(" ")[0];
      const deadlineDate = new Date(datePart);

      if (isNaN(deadlineDate.getTime())) {
        console.error("Invalid deadline date after parsing:", deadline);
        return false;
      }

      deadlineDate.setHours(0, 0, 0, 0);
      return deadlineDate >= start && deadlineDate <= end;
    } catch (error) {
      console.error("Invalid deadline date:", deadline, error);
      return false;
    }
  };

  const toggleHistory = (recordId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setOpenHistoryId((prevId) => (prevId === recordId ? null : recordId));
  };

  // Get all users
  const allUsers = React.useMemo(() => {
    if (!valueuser) return [];

    const users = [];
    Object.keys(valueuser).forEach((key) => {
      if (Array.isArray(valueuser[key])) {
        users.push(...valueuser[key]);
      }
    });

    return users;
  }, [valueuser]);

  const getUserNameById = (userId) => {
    const foundUser = allUsers.find((user) => user.cs_user_id === userId);
    return foundUser ? foundUser.cs_user_name : userId || "Unassigned";
  };

  // Form state
  const [formData, setFormData] = useState({
    id: "",
    sl: "",
    PT_id: "",
    user_id: "",
    policy: "",
    department: "",
    officer: "",
    priority: "Medium",
    status: "Drafted",
    remarks: "",
    deadline: "",
    review: "",
    nudge: "",
    assigned_to: [],
    assigned_by: "",
  });

  // Helper to get assigned user names
  const getAssignedToNames = (assignedTo) => {
    let ids = assignedTo;
    if (!ids) return [];

    if (typeof ids === "string") {
      if (ids.includes(",")) {
        ids = ids
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id !== "");
      } else if (ids.trim() !== "") {
        ids = [ids.trim()];
      } else {
        ids = [];
      }
    } else if (!Array.isArray(ids)) {
      ids = [];
    }

    return ids.map((userId) => getUserNameById(userId));
  };

  const getAssignedToNamesString = (assignedTo) => {
    const names = getAssignedToNames(assignedTo);
    if (names.length === 0) return "Unassigned";
    return names.join(", ");
  };

  // Get assigned user badges
  const getAssignedUserBadges = (assignedTo) => {
    const names = getAssignedToNames(assignedTo);
    if (names.length === 0) return null;

    return names.map((name, index) => (
      <span key={index} className="assigned-user-badge">
        {name}
      </span>
    ));
  };

  // Handle user selection
  const handleUserSelection = (userId) => {
    setFormData((prev) => {
      const currentAssigned = prev.assigned_to || [];
      const isSelected = currentAssigned.includes(userId);

      if (isSelected) {
        return {
          ...prev,
          assigned_to: currentAssigned.filter((id) => id !== userId),
        };
      } else {
        return {
          ...prev,
          assigned_to: [...currentAssigned, userId],
        };
      }
    });
  };

  // Initialize form with user data
  useEffect(() => {
    if (userData?.user?.user_id) {
      setFormData((prev) => ({
        ...prev,
        assigned_by: userData.user.user_id,
        user_id: userData.user.user_id,
        assigned_to: [],
      }));
    }
  }, [userData]);

  // Format date for display
  const formatDateForDisplay = (apiDate) => {
    if (!apiDate || apiDate === "N/A") return "N/A";
    try {
      return apiDate.split(" ")[0];
    } catch (error) {
      console.error("Error formatting date:", apiDate, error);
      return apiDate;
    }
  };

  // ========== PERMISSION FUNCTIONS ==========

  // Check if user can edit details (only assigner/creator)
  const canUserEditDetails = (policy) => {
    if (!policy || !userData?.user?.user_id) return false;
    return userData.user.user_id === policy.assigned_by;
  };

  // Check if user can comment (assigner or assigned users)
  const canUserComment = (policy) => {
    if (!policy || !userData?.user?.user_id) return false;

    const currentUserId = userData.user.user_id;
    const assignedTo = Array.isArray(policy.assigned_to)
      ? policy.assigned_to
      : [policy.assigned_to];

    return (
      currentUserId === policy.assigned_by || assignedTo.includes(currentUserId)
    );
  };

  // Check if user is the creator
  const isUserCreator = (policy) => {
    if (!policy || !userData?.user?.user_id) return false;
    return userData.user.user_id === policy.assigned_by;
  };

  // Check if user can access (for viewing)
  const canUserAccessPolicy = (policy) => {
    if (!policy || !userData?.user?.user_id) return false;

    const currentUserId = userData.user.user_id;
    const assignedTo = Array.isArray(policy.assigned_to)
      ? policy.assigned_to
      : [policy.assigned_to];

    return (
      currentUserId === policy.assigned_by || assignedTo.includes(currentUserId)
    );
  };

  // ========== CHAT FUNCTIONS ==========

  // Fetch chat messages for a policy
  const fetchChatMessages = async (policyId) => {
    try {
      if (!userData?.user?.user_id) return;

      const response = await axios.get(
        `${gapi}/chat?record_id=${policyId}&user_id=${userData.user.user_id}&type=policy`
      );

      let messages = [];
      if (Array.isArray(response.data)) {
        messages = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        messages = response.data.data;
      } else if (
        response.data.response &&
        Array.isArray(response.data.response)
      ) {
        messages = response.data.response;
      }

      setChatMessages(messages);
    } catch (error) {
      console.error("❌ Error fetching chat messages:", error);
      setChatMessages([]);
      setToast({
        message: `Error loading chat messages: ${
          error.response?.data?.errorMessage || "Internal Server Error"
        }`,
        type: "error",
      });
    }
  };

  // Send a new message
  const sendMessage = async () => {
    if (!newMessage.trim()) {
      setToast({
        message: "Please enter a message",
        type: "warning",
      });
      return;
    }

    if (!formData.PT_id || !userData?.user?.user_id) {
      setToast({
        message: "Unable to send message. Missing required data.",
        type: "error",
      });
      return;
    }

    try {
      const messageData = {
        record_id: formData.PT_id,
        user_id: userData.user.user_id,
        user_name: getUserNameById(userData.user.user_id),
        message: newMessage.trim(),
        timestamp: new Date().toISOString(),
        type: "policy",
      };

      const response = await axios.post(`${gapi}/chat`, messageData);

      if (response.data.success) {
        const newMessageObj = {
          ...messageData,
          message_id: response.data.message_id || Date.now(),
        };

        setChatMessages((prev) => [...prev, newMessageObj]);
        setNewMessage("");

        setToast({
          message: "Message sent successfully",
          type: "success",
        });

        // Update the policy's last activity
        setPolicies((prev) =>
          prev.map((policy) =>
            policy.PT_id === formData.PT_id
              ? { ...policy, updated_at: new Date().toISOString() }
              : policy
          )
        );
      } else {
        setToast({
          message: `Failed to send message: ${
            response.data.error || "Unknown error"
          }`,
          type: "error",
        });
      }
    } catch (error) {
      console.error("❌ Error sending message:", error);
      setToast({
        message: `Error sending message: ${
          error.response?.data?.errorMessage || "Internal Server Error"
        }`,
        type: "error",
      });
    }
  };

  // ========== API FUNCTIONS ==========

  // Fetch policies
  const fetchPolicies = async () => {
    try {
      if (!userData?.user?.user_id) {
        return false;
      }

      const response = await axios.get(
        `${gapi}/PT?user_id=${userData.user.user_id}&user_level=${userData.user.cs_user_level}`
      );
      const ptdata = response.data.response;

      const mappedData = ptdata.map((item, index) => {
        let assignedToArray = [];
        if (item.assigned_to) {
          if (typeof item.assigned_to === "string") {
            if (item.assigned_to.includes(",")) {
              assignedToArray = item.assigned_to
                .split(",")
                .map((id) => id.trim())
                .filter((id) => id !== "");
            } else if (item.assigned_to.trim() !== "") {
              assignedToArray = [item.assigned_to.trim()];
            }
          } else if (Array.isArray(item.assigned_to)) {
            assignedToArray = item.assigned_to;
          }
        }

        return {
          id: item.id,
          PT_id: item.PT_id,
          user_id: item.user_id,
          sl: index + 1,
          policy: item.policy_name || "Unnamed Policy",
          department: item.department_in_charge || "No Department",
          officer: item.nodal_officer || "No Officer",
          priority: item.priority || "Medium",
          status: item.current_status || "Drafted",
          deadline: item.tentative_deadline || "N/A",
          remarks: item.activity_status || "",
          review: item.last_review_date || "N/A",
          nudge: item.nudge || "No next steps",
          assigned_to: assignedToArray,
          assigned_to_name: getAssignedToNamesString(assignedToArray),
          assigned_by: item.assigned_by || "",
          created_at: item.created_at,
          updated_at: item.updated_at,
          comment: item.comment || "",
          isEdited: !!item.updated_at && item.updated_at !== item.created_at,
          deleted: item.deleted || false,
        };
      });
      setPolicies(mappedData);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const fetchDeletedPolicies = async () => {
    try {
      if (!userData?.user?.user_id) {
        return false;
      }

      const response = await axios.get(
        `${gapi}/PT?user_id=${userData.user.user_id}&deleted=true&user_level=${userData.user.cs_user_level}`
      );
      const ptdata = response.data.response;

      const mappedData = ptdata.map((item, index) => {
        let assignedToArray = [];
        if (item.assigned_to) {
          if (typeof item.assigned_to === "string") {
            if (item.assigned_to.includes(",")) {
              assignedToArray = item.assigned_to
                .split(",")
                .map((id) => id.trim())
                .filter((id) => id !== "");
            } else if (item.assigned_to.trim() !== "") {
              assignedToArray = [item.assigned_to.trim()];
            }
          } else if (Array.isArray(item.assigned_to)) {
            assignedToArray = item.assigned_to;
          }
        }

        return {
          id: item.id,
          PT_id: item.PT_id,
          user_id: item.user_id,
          sl: index + 1,
          policy: item.policy_name || "Unnamed Policy",
          department: item.department_in_charge || "No Department",
          officer: item.nodal_officer || "No Officer",
          priority: item.priority || "Medium",
          status: item.current_status || "Drafted",
          deadline: item.tentative_deadline || "N/A",
          remarks: item.activity_status || "",
          review: item.last_review_date || "N/A",
          nudge: item.nudge || "No next steps",
          assigned_to: assignedToArray,
          assigned_to_name: getAssignedToNamesString(assignedToArray),
          assigned_by: item.assigned_by || "",
          deleted: true,
        };
      });
      setDeleted(mappedData);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  // Update summary counts
  const updateSummaryCounts = (updatedPolicies) => {
    if (!setSummaryInfo) return;

    const today = new Date();

    const importantCount = updatedPolicies.filter(
      (p) => !p.deleted && p.status && p.status.toLowerCase() === "important"
    ).length;

    const pendingCount = updatedPolicies.filter(
      (p) => !p.deleted && isPolicyPending(p)
    ).length;

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const dueThisWeekCount = updatedPolicies.filter((p) => {
      if (p.deleted || !isPolicyPending(p)) return false;
      return isDeadlineInRange(p.deadline, startOfWeek, endOfWeek);
    }).length;

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const dueThisMonthCount = updatedPolicies.filter((p) => {
      if (p.deleted || !isPolicyPending(p)) return false;
      return isDeadlineInRange(p.deadline, startOfMonth, endOfMonth);
    }).length;

    setSummaryInfo((prev) => ({
      ...prev,
      policies: [
        { label: "Important", value: importantCount },
        { label: "Pending", value: pendingCount },
        { label: "Due This Week", value: dueThisWeekCount },
        { label: "Due this month", value: dueThisMonthCount },
      ],
    }));
  };

  // Fetch data when user data is ready
  useEffect(() => {
    const fetchData = async () => {
      if (!userData?.user?.user_id) {
        return;
      }

      setIsLoading(true);
      try {
        await Promise.all([fetchPolicies(), fetchDeletedPolicies()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!userLoading) {
      fetchData();
    }
  }, [userData, userLoading, allUsers]);

  // Update summary counts whenever policies change
  useEffect(() => {
    updateSummaryCounts(policies);
  }, [policies]);

  useEffect(() => {
    if (editingPolicy && allUsers.length > 0) {
      setFormData((prev) => ({
        ...prev,
        assigned_to: editingPolicy.assigned_to || [],
      }));
    }
  }, [editingPolicy, allUsers]);

  // Filtered data
  const filteredPolicies = policies
    .filter((policy) => !policy.deleted)
    .filter((policy) => {
      if (!filter || filter.trim() === "") return true;

      const dateRangeFilter = getDateRangeFilter(filter);

      if (dateRangeFilter) {
        const isPending = isPolicyPending(policy);
        const inRange = isDeadlineInRange(
          policy.deadline,
          dateRangeFilter.start,
          dateRangeFilter.end
        );

        if (!isPending) return false;
        return inRange;
      }

      const policyName = policy.policy || "";
      const department = policy.department || "";
      const status = policy.status || "";
      const officer = policy.officer || "";
      const assignedTo = policy.assigned_to_name || "";

      return (
        policyName.toLowerCase().includes(filter.toLowerCase()) ||
        department.toLowerCase().includes(filter.toLowerCase()) ||
        status.toLowerCase().includes(filter.toLowerCase()) ||
        officer.toLowerCase().includes(filter.toLowerCase()) ||
        assignedTo.toLowerCase().includes(filter.toLowerCase())
      );
    });

  const filteredDeleted = deleted.filter((deletedPolicy) => {
    if (!filter || filter.trim() === "") return true;

    const dateRangeFilter = getDateRangeFilter(filter);

    if (dateRangeFilter) {
      const isPending = isPolicyPending(deletedPolicy);
      const inRange = isDeadlineInRange(
        deletedPolicy.deadline,
        dateRangeFilter.start,
        dateRangeFilter.end
      );

      if (!isPending) return false;
      return inRange;
    }

    const policyName = deletedPolicy.policy || "";
    const department = deletedPolicy.department || "";
    const status = deletedPolicy.status || "";
    const officer = deletedPolicy.officer || "";
    const assignedTo = deletedPolicy.assigned_to_name || "";

    return (
      policyName.toLowerCase().includes(filter.toLowerCase()) ||
      department.toLowerCase().includes(filter.toLowerCase()) ||
      status.toLowerCase().includes(filter.toLowerCase()) ||
      officer.toLowerCase().includes(filter.toLowerCase()) ||
      assignedTo.toLowerCase().includes(filter.toLowerCase())
    );
  });

  // Handle Add New
  const handleAddNew = () => {
    setShowForm(true);
    setEditingPolicy(null);
    setFormData({
      sl: policies.length + 1,
      policy: "",
      department: "",
      officer: "",
      priority: "Medium",
      status: "Drafted",
      remarks: "",
      deadline: "",
      review: "",
      nudge: "",
      assigned_to: [],
      assigned_by: userData?.user?.user_id || "",
      user_id: userData?.user?.user_id || "",
    });
    // Clear chat when adding new
    setChatMessages([]);
    setNewMessage("");
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPolicy(null);
    setChatMessages([]);
    setNewMessage("");
  };

  // Handle Edit - with permission check
  const handleEdit = async (index) => {
    const policy = policies[index];

    if (!canUserAccessPolicy(policy)) {
      setToast({
        message: "You don't have permission to access this policy.",
        type: "error",
      });
      return;
    }

    console.log("✏️ Editing policy:", policy);
    setEditingPolicy(policy);
    setShowForm(true);
    setPreviousFormData(policy);

    let assignedToArray = policy.assigned_to;
    if (!assignedToArray) {
      assignedToArray = [];
    } else if (typeof assignedToArray === "string") {
      if (assignedToArray.includes(",")) {
        assignedToArray = assignedToArray
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id !== "");
      } else if (assignedToArray.trim() !== "") {
        assignedToArray = [assignedToArray.trim()];
      } else {
        assignedToArray = [];
      }
    }

    setFormData({
      ...policy,
      assigned_to: assignedToArray,
    });

    // Fetch chat messages when opening edit
    await fetchChatMessages(policy.PT_id);
  };

  // Handle Save
  const handleSave = async () => {
    console.log("click register", formData);
    if (!formData.policy?.trim()) {
      setToast({
        message: `Please enter Policy Name`,
        type: "error",
      });
      return;
    }
    if (!formData.department?.trim()) {
      setToast({
        message: `Please enter Department in Charge`,
        type: "error",
      });
      return;
    }
    if (!formData.officer?.trim()) {
      setToast({
        message: `Please enter Nodal Officer`,
        type: "error",
      });
      return;
    }
    if (!formData.priority) {
      setToast({
        message: `Please select Priority`,
        type: "error",
      });
      return;
    }
    if (!formData.status) {
      setToast({
        message: `Please select Status`,
        type: "error",
      });
      return;
    }
    if (!formData.deadline) {
      setToast({
        message: `Please select Tentative Deadline`,
        type: "error",
      });
      return;
    }
    if (!formData.review) {
      setToast({
        message: `Please select Last Review Date`,
        type: "error",
      });
      return;
    }
    if (!formData.nudge?.trim()) {
      setToast({
        message: `Please enter Next Action / Nudge`,
        type: "error",
      });
      return;
    }
    if (!formData.remarks?.trim()) {
      setToast({
        message: `Please enter Activity Status / Remarks`,
        type: "error",
      });
      return;
    }
    if (!formData.assigned_to || formData.assigned_to.length === 0) {
      setToast({
        message: `Please select at least one assignee`,
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);

    const assignedToIds = formData.assigned_to || [];

    const apiData = {
      data: {
        policy_name: formData.policy,
        department_in_charge: formData.department,
        nodal_officer: formData.officer,
        priority: formData.priority || "Medium",
        current_status: formData.status || "Drafted",
        tentative_deadline: formData.deadline,
        activity_status: formData.remarks,
        last_review_date: formData.review,
        nudge: formData.nudge,
        assigned_to: assignedToIds,
        assigned_by: formData.assigned_by,
        user_id: userData?.user?.user_id,
      },
    };

    console.log("Sending API data:", apiData);

    try {
      let response;
      let ptid;
      if (editingPolicy) {
        response = await axios.put(
          `${gapi}/PT?PT_id=${editingPolicy.PT_id}&user_id=${userData?.user?.user_id}`,
          {
            PT_id: editingPolicy.PT_id,
            updates: apiData.data,
          }
        );
        setToast({
          message: `${formData.policy} updated successfully`,
          type: "success",
        });
        ptid = editingPolicy.PT_id;
      } else {
        response = await axios.post(
          `${gapi}/PT?user_id=${userData?.user?.user_id}`,
          apiData
        );
        setToast({
          message: `${formData.policy} created successfully`,
          type: "success",
        });
        ptid = response.data.inserted_id;
      }

      const nextSl =
        policies.length > 0 ? Math.max(...policies.map((p) => p.sl)) + 1 : 1;

      const newPolicy = {
        id: editingPolicy ? editingPolicy.id : Date.now(),
        PT_id: ptid,
        sl: editingPolicy ? formData.sl : nextSl,
        policy: formData.policy,
        department: formData.department,
        officer: formData.officer,
        priority: formData.priority || "Medium",
        status: formData.status,
        deadline: formData.deadline,
        remarks: formData.remarks,
        review: formData.review,
        nudge: formData.nudge,
        assigned_to: assignedToIds,
        assigned_to_name: getAssignedToNamesString(assignedToIds),
        assigned_by: formData.assigned_by,
        user_id: userData?.user?.user_id,
        created_at: editingPolicy
          ? editingPolicy.created_at
          : new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isEdited: true,
        deleted: false,
      };

      let updatedPolicies;
      if (editingPolicy) {
        updatedPolicies = policies.map((p) =>
          p.PT_id === editingPolicy.PT_id ? newPolicy : p
        );
        setPolicies(updatedPolicies);
      } else {
        updatedPolicies = [newPolicy, ...policies];
        setPolicies(updatedPolicies);
      }

      updateSummaryCounts(updatedPolicies);

      setShowForm(false);
      setEditingPolicy(null);
      setExpandedRow(null);
      setChatMessages([]);
      setNewMessage("");
    } catch (error) {
      console.error("Error saving policy:", error);
      setToast({
        message: `${
          error.response?.data?.errorMessage || "Internal Server Error"
        }`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (PT_id) => {
    if (!PT_id) {
      setToast({
        message: "Invalid policy ID",
        type: "error",
      });
      return;
    }

    const permanent = recycleBin ? true : false;
    if (
      window.confirm(
        `${
          recycleBin
            ? "Delete this policy permanently"
            : "Delete this policy into recycle bin"
        }`
      )
    ) {
      try {
        const response = await axios.delete(
          `${gapi}/PT?user_id=${userData?.user?.user_id}&PT_id=${PT_id}&permanent=${permanent}`
        );

        let updatedPolicies;
        if (recycleBin) {
          setDeleted(deleted.filter((p) => p.PT_id !== PT_id));
          updatedPolicies = policies;
        } else {
          const policyToDelete = policies.find((p) => p.PT_id === PT_id);

          if (policyToDelete) {
            updatedPolicies = policies.filter((p) => p.PT_id !== PT_id);
            setPolicies(updatedPolicies);
            setDeleted((prevDeleted) => [
              { ...policyToDelete, deleted: true },
              ...prevDeleted,
            ]);
          } else {
            updatedPolicies = policies;
          }
        }

        if (expandedRow === PT_id) {
          setExpandedRow(null);
        }

        updateSummaryCounts(updatedPolicies);

        setToast({
          message: `Policy ${
            recycleBin ? "permanently deleted" : "moved to recycle bin"
          } successfully`,
          type: "success",
        });
      } catch (error) {
        setToast({
          message: `${error.errorMessage || "Internal Server Error"} `,
          type: "error",
        });
      }
    }
  };

  const handleRowClick = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getPriorityBadgeClass = (priority) => {
    if (!priority) return "priority-badge priority-badge-medium";
    const priorityLower = priority.toLowerCase();
    if (priorityLower === "high") return "priority-badge priority-badge-high";
    if (priorityLower === "medium")
      return "priority-badge priority-badge-medium";
    if (priorityLower === "low") return "priority-badge priority-badge-low";
    return "priority-badge priority-badge-medium";
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return "status-badge status-badge-drafted";
    const statusLower = status.toLowerCase();

    if (statusLower === "completed")
      return "status-badge status-badge-completed";
    if (statusLower === "under implementation")
      return "status-badge status-badge-implementation";
    if (statusLower === "approved") return "status-badge status-badge-approved";
    if (statusLower === "drafted") return "status-badge status-badge-drafted";
    if (statusLower === "pending") return "status-badge status-badge-pending";
    if (statusLower === "important")
      return "status-badge status-badge-important";
    if (statusLower === "critical") return "status-badge status-badge-critical";

    return "status-badge status-badge-drafted";
  };

  const clearFilter = () => {
    setFilter("");
  };

  const toggleRecycleBin = () => {
    setRecycleBin((prev) => !prev);
    setShowForm(false);
    setEditingPolicy(null);
    setExpandedRow(null);
    setChatMessages([]);
    setNewMessage("");
  };

  // Show loading/error states
  if (userLoading) {
    return (
      <div className="policy-page-content">
        <div className="policy-card">
          <div className="policy-loading">Loading user session...</div>
        </div>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="policy-page-content">
        <div className="policy-card">
          <div className="policy-error">
            <h3>Authentication Required</h3>
            <p>Please log in to access policy features.</p>
            <button
              onClick={() => (window.location.href = "/login")}
              className="policy-btn"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="policy-page-content">
        <div className="policy-card">
          <div className="policy-loading">Loading policies...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="policy-page-content">
      <div className="policy-card">
        {/* Toast Component */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            duration={3000}
            onClose={() => setToast(null)}
          />
        )}

        <h1 className="policy-title">
          Policy Tracker {recycleBin && " > recycle bin"}
        </h1>

        {/* Show active filter badge */}
        {filter && (
          <div className="active-filter-badge">
            <span>
              🔍 Filtering by: <strong>{filter}</strong>
              {filterToPage && filterToPage === filter && " (from dashboard)"}
            </span>
            <button onClick={() => setFilter("")} className="clear-filter-btn">
              Clear Filter
            </button>
          </div>
        )}

        <div className="policy-controls">
          <input
            type="text"
            className="policy-filter-input"
            placeholder={
              recycleBin
                ? "Filter deleted policies..."
                : "Filter by policy / department / status / assigned to"
            }
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button
            className="policy-btn policy-btn-ghost policy-btn-small"
            onClick={clearFilter}
          >
            Clear
          </button>
          {!showForm && (
            <>
              {!recycleBin && (
                <button
                  className="policy-btn policy-btn-small"
                  onClick={handleAddNew}
                >
                  Add new
                </button>
              )}
              <button
                className="policy-btn policy-btn-small"
                onClick={toggleRecycleBin}
              >
                {recycleBin ? "Back to Policies" : "Recycle Bin"}
              </button>
            </>
          )}
        </div>

        {/* Enhanced Form with Conditional Layout */}
        {showForm && !recycleBin && (
          <div
            className={`policy-form-wrap enhanced-form ${
              editingPolicy === null ? "full-width-form" : "split-form"
            }`}
          >
            <div className="form-header">
              <h3 className="form-title">
                {editingPolicy !== null
                  ? `📋 ${formData.policy || "Untitled Policy"}`
                  : "➕ Add New Policy"}
                <button
                  className="close-form-btn"
                  onClick={handleCancel}
                  title="Close form"
                >
                  ×
                </button>
              </h3>
            </div>

            <div className="form-layout">
              {/* Policy Details Section - Always visible */}
              <div className="details-section">
                <div className="form-section">
                  <h4 className="form-section-title">Basic Information</h4>
                  <div className="policy-form-row">
                    <div className="form-group">
                      <label htmlFor="sl-no" className="form-label">
                        Sl No <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="sl-no"
                        className="policy-input"
                        placeholder="Sl No"
                        value={formData.sl}
                        onChange={(e) =>
                          handleInputChange(
                            "sl",
                            parseInt(e.target.value) || ""
                          )
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="policy-name" className="form-label">
                        Policy Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="policy-name"
                        className="policy-input"
                        placeholder="Policy Name"
                        value={formData.policy}
                        onChange={(e) =>
                          handleInputChange("policy", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="department" className="form-label">
                        Department in Charge <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="department"
                        className="policy-input"
                        placeholder="Department in Charge"
                        value={formData.department}
                        onChange={(e) =>
                          handleInputChange("department", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                  </div>

                  <div className="policy-form-row">
                    <div className="form-group">
                      <label htmlFor="officer" className="form-label">
                        Nodal Officer <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="officer"
                        className="policy-input"
                        placeholder="Nodal Officer"
                        value={formData.officer}
                        onChange={(e) =>
                          handleInputChange("officer", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="priority" className="form-label">
                        Priority <span className="required">*</span>
                      </label>
                      <select
                        id="priority"
                        className="policy-select"
                        value={formData.priority}
                        onChange={(e) =>
                          handleInputChange("priority", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="status" className="form-label">
                        Current Status <span className="required">*</span>
                      </label>
                      <select
                        id="status"
                        className="policy-select"
                        value={formData.status}
                        onChange={(e) =>
                          handleInputChange("status", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      >
                        <option value="Drafted">Drafted</option>
                        <option value="Approved">Approved</option>
                        <option value="Under Implementation">
                          Under Implementation
                        </option>
                        <option value="Important">Important</option>
                        <option value="Pending">Pending</option>
                        <option value="Critical">Critical</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 className="form-section-title">Dates</h4>
                  <div className="policy-form-row">
                    <div className="form-group">
                      <label htmlFor="deadline" className="form-label">
                        Tentative Deadline <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        id="deadline"
                        className="policy-input"
                        placeholder="Tentative Deadline"
                        value={formData.deadline}
                        onChange={(e) =>
                          handleInputChange("deadline", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="review" className="form-label">
                        Last Review Date <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        id="review"
                        className="policy-input"
                        placeholder="Last Review Date"
                        value={formData.review}
                        onChange={(e) =>
                          handleInputChange("review", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 className="form-section-title">Assignment</h4>
                  <div className="policy-form-row-full">
                    <div className="user-selection-container">
                      <label className="form-label">
                        Assigned To: <span className="required">*</span>
                      </label>
                      <div className="user-selection-grid">
                        {allUsers
                          .filter((user) => {
                            // Filter out current user (don't show them in selection)
                            const currentUserId = userData?.user?.user_id;
                            if (
                              currentUserId &&
                              user.cs_user_id === currentUserId
                            ) {
                              return false;
                            }
                            return true;
                          })
                          .map((user) => (
                            <div
                              key={user.cs_user_id}
                              className={`user-option ${
                                formData.assigned_to?.includes(user.cs_user_id)
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => {
                                if (
                                  editingPolicy !== null &&
                                  !canUserEditDetails(editingPolicy)
                                )
                                  return;
                                handleUserSelection(user.cs_user_id);
                              }}
                            >
                              <div className="user-checkbox">
                                {formData.assigned_to?.includes(
                                  user.cs_user_id
                                ) && <div className="checkmark">✓</div>}
                              </div>
                              <span className="user-name">
                                {user.cs_user_name}
                              </span>
                            </div>
                          ))}
                      </div>
                      {formData.assigned_to &&
                        formData.assigned_to.length > 0 && (
                          <div className="selected-users">
                            <strong>Selected: </strong>
                            {getAssignedToNames(formData.assigned_to).join(
                              ", "
                            )}
                          </div>
                        )}
                      {(!formData.assigned_to ||
                        formData.assigned_to.length === 0) && (
                        <div className="selection-warning">
                          Please select at least one user
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 className="form-section-title">Details</h4>
                  <div className="policy-form-row">
                    <div className="form-group">
                      <label htmlFor="nudge" className="form-label">
                        Next Action / Nudge <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="nudge"
                        className="policy-input"
                        placeholder="Next Action / Nudge"
                        value={formData.nudge}
                        onChange={(e) =>
                          handleInputChange("nudge", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                  </div>

                  <div className="policy-form-row">
                    <div className="form-group full-width">
                      <label htmlFor="remarks" className="form-label">
                        Activity Status / Remarks{" "}
                        <span className="required">*</span>
                      </label>
                      <textarea
                        id="remarks"
                        className="policy-textarea"
                        rows="4"
                        placeholder="Activity Status / Remarks"
                        value={formData.remarks}
                        onChange={(e) =>
                          handleInputChange("remarks", e.target.value)
                        }
                        disabled={
                          editingPolicy !== null &&
                          !canUserEditDetails(editingPolicy)
                        }
                      />
                    </div>
                  </div>
                </div>

                {editingPolicy !== null &&
                  !canUserEditDetails(editingPolicy) && (
                    <div className="permission-notice">
                      <span>
                        📋 You can view details but only the creator can edit
                        them
                      </span>
                    </div>
                  )}
              </div>

              {/* Chat Section - Only in Edit Mode */}
              {editingPolicy !== null && (
                <div className="chat-section">
                  <h4>💬 Discussion ({chatMessages.length})</h4>
                  <div className="chat-container-inline">
                    <div className="chat-messages-inline">
                      {chatMessages.length === 0 ? (
                        <div className="no-messages">
                          No messages yet. Start the conversation!
                        </div>
                      ) : (
                        chatMessages.map((message) => (
                          <div
                            key={message.message_id || message.timestamp}
                            className={`message ${
                              message.user_id === userData.user.user_id
                                ? "own-message"
                                : "other-message"
                            }`}
                          >
                            <div className="message-header">
                              <strong>{message.user_name}</strong>
                              <span className="message-time">
                                {new Date(message.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className="message-content">
                              {message.message}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {canUserComment(editingPolicy) && (
                      <div className="chat-input-container-inline">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="chat-input-inline"
                          rows="3"
                        />
                        <button
                          onClick={sendMessage}
                          className="send-message-btn-inline"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Send"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="policy-form-actions">
              {/* Show Save/Update button only for creator or when adding new */}
              {(editingPolicy === null ||
                canUserEditDetails(editingPolicy)) && (
                <button
                  className="policy-btn policy-btn-primary"
                  onClick={handleSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner33"></div>
                      {editingPolicy !== null ? "Updating..." : "Adding..."}
                    </>
                  ) : editingPolicy !== null ? (
                    "Update Policy"
                  ) : (
                    "Save Policy"
                  )}
                </button>
              )}
              <button
                className="policy-btn policy-btn-ghost"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Conditional Rendering (Active or Bin) */}
        {!recycleBin ? (
          <>
            {/* Active Policies Section */}
            <h3>Active Policies ({filteredPolicies.length})</h3>
            {filteredPolicies.length === 0 ? (
              <div className="policy-no-data">
                {filter
                  ? `No policies found matching "${filter}"`
                  : "No policies found. Click 'Add new' to create your first policy."}
              </div>
            ) : isMobile ? (
              /* Mobile Card View */
              <div className="policy-cards-container">
                {filteredPolicies.map((policy, index) => (
                  <div
                    key={policy.PT_id || index}
                    className={`policy-card-item ${
                      expandedRow === policy.PT_id ? "expanded" : ""
                    }`}
                    onClick={() => handleRowClick(policy.PT_id)}
                  >
                    <div className="policy-card-header">
                      <div className="policy-card-sl">{policy.sl}.</div>
                      <div className="policy-card-policy">{policy.policy}</div>
                      <div className={getPriorityBadgeClass(policy.priority)}>
                        {policy.priority}
                      </div>
                    </div>

                    <div className="policy-card-details">
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Department:</span>
                        <span className="policy-card-value">
                          {policy.department}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Officer:</span>
                        <span className="policy-card-value">
                          {policy.officer}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Status:</span>
                        <span className={getStatusBadgeClass(policy.status)}>
                          {policy.status}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Deadline:</span>
                        <span className="policy-card-value">
                          {formatDateForDisplay(policy.deadline)}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Assigned To:</span>
                        <div className="assigned-users-container-mobile">
                          {getAssignedUserBadges(policy.assigned_to) ||
                            "Unassigned"}
                        </div>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Nudge:</span>
                        <span className="policy-card-value">
                          {policy.nudge || "-"}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Remarks:</span>
                        <span className="policy-card-value">
                          {policy.remarks || "-"}
                        </span>
                      </div>
                    </div>

                    {expandedRow === policy.PT_id && (
                      <div className="policy-card-expanded-content">
                        <div className="policy-card-expanded-item">
                          <span className="policy-card-expanded-label">
                            Assigned To:
                          </span>
                          <span className="policy-card-expanded-value">
                            {policy.assigned_to_name}
                          </span>
                        </div>
                        <div className="policy-card-expanded-item">
                          <span className="policy-card-expanded-label">
                            Assigned By:
                          </span>
                          <span className="policy-card-expanded-value">
                            {getUserNameById(policy.assigned_by)}
                          </span>
                        </div>
                        <div className="policy-card-expanded-item">
                          <span className="policy-card-expanded-label">
                            Tentative Date:
                          </span>
                          <span className="policy-card-expanded-value">
                            {formatDateForDisplay(policy.deadline) ||
                              "No review date"}
                          </span>
                        </div>
                        <div className="policy-card-expanded-item">
                          <span className="policy-card-expanded-label">
                            Full Remarks:
                          </span>
                          <span className="policy-card-expanded-value">
                            {policy.remarks || "No remarks"}
                          </span>
                        </div>
                        <div className="policy-card-expanded-item">
                          <span className="policy-card-expanded-label">
                            Next Actions:
                          </span>
                          <span className="policy-card-expanded-value">
                            {policy.nudge || "No next actions"}
                          </span>
                        </div>
                        <div className="policy-card-expanded-item">
                          <span className="policy-card-expanded-label">
                            Review Notes:
                          </span>
                          <span className="policy-card-expanded-value">
                            Last reviewed on{" "}
                            {formatDateForDisplay(policy.review) ||
                              "No review date"}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="policy-card-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHistory(policy.PT_id, e);
                        }}
                        className="policy-btn policy-btn-small"
                      >
                        History
                      </button>
                      {openHistoryId === policy.PT_id && (
                        <History
                          record_id={policy.PT_id}
                          setHistoryOpen={setOpenHistoryId}
                        />
                      )}
                      {canUserAccessPolicy(policy) ? (
                        <>
                          {/* Show comment icon for assigned users who are not creators */}
                          {!isUserCreator(policy) ? (
                            <button
                              className="iconcon comment-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const originalIndex = policies.findIndex(
                                  (p) => p.PT_id === policy.PT_id
                                );
                                handleEdit(originalIndex);
                              }}
                              title="Add Comment"
                            >
                              💬
                            </button>
                          ) : (
                            /* Show edit icon for creator */
                            <button
                              className="iconcon"
                              onClick={(e) => {
                                e.stopPropagation();
                                const originalIndex = policies.findIndex(
                                  (p) => p.PT_id === policy.PT_id
                                );
                                handleEdit(originalIndex);
                              }}
                              title="Edit & Chat"
                            >
                              <Editic />
                            </button>
                          )}
                          {isUserCreator(policy) && (
                            <button
                              className="iconcon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(policy.PT_id);
                              }}
                              title="Move to Bin"
                            >
                              <Deleteic />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="not-authorized-text">
                          Not authorized
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop Table View */
              <div className="policy-table-container">
                <div className="table-scroll-wrapper">
                  <table className="policy-table">
                    <thead>
                      <tr>
                        <th className="col-sl">Sl No</th>
                        <th className="col-policy">Policy Name</th>
                        <th className="col-dept">Department</th>
                        <th className="col-officer">Nodal Officer</th>
                        <th className="col-priority">Priority</th>
                        <th className="col-status">Status</th>
                        <th className="col-deadline">Deadline</th>
                        <th className="col-assigned">Assigned To</th>
                        <th className="col-nudge">Nudge</th>
                        <th className="col-remarks">Remarks</th>
                        <th className="col-actions">Actions</th>
                        <th className="col-history">History</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPolicies.map((policy, index) => (
                        <React.Fragment key={policy.PT_id || index}>
                          <tr
                            className={`policy-table-row ${
                              expandedRow === policy.PT_id
                                ? "policy-row-expanded"
                                : ""
                            }`}
                            onClick={() => handleRowClick(policy.PT_id)}
                          >
                            <td className="col-sl">{policy.sl}</td>
                            <td className="col-policy">
                              <div className="full-content-cell">
                                {policy.policy}
                              </div>
                            </td>
                            <td className="col-dept">
                              <div className="full-content-cell">
                                {policy.department}
                              </div>
                            </td>
                            <td className="col-officer">
                              <div className="full-content-cell">
                                {policy.officer}
                              </div>
                            </td>
                            <td className="col-priority">
                              <div className="full-content-cell">
                                <span
                                  className={getPriorityBadgeClass(
                                    policy.priority
                                  )}
                                >
                                  {policy.priority}
                                </span>
                              </div>
                            </td>
                            <td className="col-status">
                              <div className="full-content-cell">
                                <span
                                  className={getStatusBadgeClass(policy.status)}
                                >
                                  {policy.status}
                                </span>
                              </div>
                            </td>
                            <td className="col-deadline">
                              <div className="full-content-cell">
                                {formatDateForDisplay(policy.deadline)}
                              </div>
                            </td>
                            <td className="col-assigned">
                              <div className="assigned-users-container">
                                {getAssignedUserBadges(policy.assigned_to) ||
                                  "Unassigned"}
                              </div>
                            </td>
                            <td className="col-nudge">
                              <div className="full-content-cell">
                                {policy.nudge || "-"}
                              </div>
                            </td>
                            <td className="col-remarks">
                              <div className="full-content-cell">
                                {policy.remarks || "-"}
                              </div>
                            </td>
                            {canUserAccessPolicy(policy) ? (
                              <td className="col-actions">
                                <div className="full-content-cell">
                                  {/* Show comment icon for assigned users who are not creators */}
                                  {!isUserCreator(policy) ? (
                                    <button
                                      className="iconcon comment-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const originalIndex =
                                          policies.findIndex(
                                            (p) => p.PT_id === policy.PT_id
                                          );
                                        handleEdit(originalIndex);
                                      }}
                                      title="Add Comment"
                                    >
                                      💬
                                    </button>
                                  ) : (
                                    /* Show edit icon for creator */
                                    <button
                                      className="iconcon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const originalIndex =
                                          policies.findIndex(
                                            (p) => p.PT_id === policy.PT_id
                                          );
                                        handleEdit(originalIndex);
                                      }}
                                      title="Edit & Chat"
                                    >
                                      <Editic />
                                    </button>
                                  )}
                                  {isUserCreator(policy) && (
                                    <button
                                      className="iconcon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(policy.PT_id);
                                      }}
                                      title="Move to Bin"
                                    >
                                      <Deleteic />
                                    </button>
                                  )}
                                </div>
                              </td>
                            ) : (
                              <td className="col-actions">
                                <div className="full-content-cell">
                                  <span className="not-authorized-text">
                                    Not authorized
                                  </span>
                                </div>
                              </td>
                            )}
                            <td className="col-history">
                              <div className="full-content-cell">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleHistory(policy.PT_id, e);
                                  }}
                                  className="policy-btn policy-btn-small"
                                >
                                  History
                                </button>
                                {openHistoryId === policy.PT_id && (
                                  <History
                                    record_id={policy.PT_id}
                                    setHistoryOpen={setOpenHistoryId}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>

                          {expandedRow === policy.PT_id && (
                            <tr className="policy-details-row">
                              <td colSpan="12">
                                <div className="policy-details-content">
                                  <div>
                                    <strong>Assigned To:</strong>{" "}
                                    {policy.assigned_to_name}
                                  </div>
                                  <div>
                                    <strong>Assigned By:</strong>{" "}
                                    {getUserNameById(policy.assigned_by)}
                                  </div>
                                  <div>
                                    <strong>Tentative Date:</strong>{" "}
                                    {formatDateForDisplay(policy.deadline) ||
                                      "No review date"}
                                  </div>
                                  <div>
                                    <strong>Full Remarks:</strong>{" "}
                                    {policy.remarks || "No remarks"}
                                  </div>
                                  <div>
                                    <strong>Detailed Next Actions:</strong>{" "}
                                    {policy.nudge || "No next actions"}
                                  </div>
                                  <div>
                                    <strong>Review Notes:</strong> Last reviewed
                                    on{" "}
                                    {formatDateForDisplay(policy.review) ||
                                      "No review date"}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Bin Section */}
            <h3>Bin ({filteredDeleted.length})</h3>
            {filteredDeleted.length === 0 ? (
              <div className="policy-no-data">
                {filter
                  ? `No deleted policies found matching "${filter}"`
                  : "Bin is empty."}
              </div>
            ) : isMobile ? (
              /* Mobile Card View for Bin */
              <div className="policy-cards-container">
                {filteredDeleted.map((policy, index) => (
                  <div
                    key={policy.PT_id || index}
                    className="policy-card-item deleted-card"
                  >
                    <div className="policy-card-header">
                      <div className="policy-card-sl">{policy.sl}.</div>
                      <div className="policy-card-policy">{policy.policy}</div>
                      <div className="deleted-badge">Deleted</div>
                    </div>

                    <div className="policy-card-details">
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Department:</span>
                        <span className="policy-card-value">
                          {policy.department}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Officer:</span>
                        <span className="policy-card-value">
                          {policy.officer}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Status:</span>
                        <span className={getStatusBadgeClass(policy.status)}>
                          {policy.status}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Deadline:</span>
                        <span className="policy-card-value">
                          {formatDateForDisplay(policy.deadline)}
                        </span>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Assigned To:</span>
                        <div className="assigned-users-container-mobile">
                          {getAssignedUserBadges(policy.assigned_to) ||
                            "Unassigned"}
                        </div>
                      </div>
                      <div className="policy-card-detail-item">
                        <span className="policy-card-label">Assigned By:</span>
                        <span className="policy-card-value">
                          {getUserNameById(policy.assigned_by)}
                        </span>
                      </div>
                    </div>

                    <div className="policy-card-actions">
                      {isUserCreator(policy) ? (
                        <button
                          className="policy-btn danger-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(policy.PT_id);
                          }}
                          title="Permanently Delete"
                        >
                          Delete Permanently
                        </button>
                      ) : (
                        <span className="not-owner-text">Not the owner</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop Table View for Bin */
              <div className="policy-table-container">
                <div className="table-scroll-wrapper">
                  <table className="policy-table">
                    <thead>
                      <tr>
                        <th className="col-sl">Sl No</th>
                        <th className="col-policy">Policy Name</th>
                        <th className="col-dept">Department</th>
                        <th className="col-officer">Nodal Officer</th>
                        <th className="col-priority">Priority</th>
                        <th className="col-status">Status</th>
                        <th className="col-deadline">Deadline</th>
                        <th className="col-assigned">Assigned To</th>
                        <th className="col-assigned-by">Assigned By</th>
                        <th className="col-nudge">Nudge</th>
                        <th className="col-remarks">Remarks</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeleted.map((policy, index) => (
                        <tr key={policy.PT_id || index}>
                          <td className="col-sl">{policy.sl}</td>
                          <td className="col-policy">
                            <div className="full-content-cell">
                              {policy.policy}
                            </div>
                          </td>
                          <td className="col-dept">
                            <div className="full-content-cell">
                              {policy.department}
                            </div>
                          </td>
                          <td className="col-officer">
                            <div className="full-content-cell">
                              {policy.officer}
                            </div>
                          </td>
                          <td className="col-priority">
                            <div className="full-content-cell">
                              <span
                                className={getPriorityBadgeClass(
                                  policy.priority
                                )}
                              >
                                {policy.priority}
                              </span>
                            </div>
                          </td>
                          <td className="col-status">
                            <div className="full-content-cell">
                              <span
                                className={getStatusBadgeClass(policy.status)}
                              >
                                {policy.status}
                              </span>
                            </div>
                          </td>
                          <td className="col-deadline">
                            <div className="full-content-cell">
                              {formatDateForDisplay(policy.deadline)}
                            </div>
                          </td>
                          <td className="col-assigned">
                            <div className="assigned-users-container">
                              {getAssignedUserBadges(policy.assigned_to) ||
                                "Unassigned"}
                            </div>
                          </td>
                          <td className="col-assigned-by">
                            <div className="full-content-cell">
                              {getUserNameById(policy.assigned_by)}
                            </div>
                          </td>
                          <td className="col-nudge">
                            <div className="full-content-cell">
                              {policy.nudge || "-"}
                            </div>
                          </td>
                          <td className="col-remarks">
                            <div className="full-content-cell">
                              {policy.remarks || "-"}
                            </div>
                          </td>
                          <td className="col-actions">
                            <div className="full-content-cell">
                              {isUserCreator(policy) ? (
                                <button
                                  className="iconcon danger"
                                  onClick={() => handleDelete(policy.PT_id)}
                                  title="Permanently Delete"
                                >
                                  <Deleteic />
                                </button>
                              ) : (
                                <span className="not-owner-text">
                                  Not the owner
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PolicyTracker;
